import React from 'react';
import PropTypes from 'prop-types';

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from 'views/components';
import {KeyboardArrowDown as ExpandIcon, KeyboardArrowUp as CollapseIcon} from 'views/icons';
import {InvitationFamilyAnswers, Translate, translate} from 'views/module';

import moment from 'moment';

const COLUMNS = 'minmax(160px, 1.2fr) 120px 160px 170px 170px minmax(200px, 1.4fr) 170px';

const caregiverName = (invitation) => `${invitation.caregiver.person.firstName} ${invitation.caregiver.person.lastName}`;

// Only a completed interview has family answers to play and share.
const hasFamilyAnswers = (invitation) => invitation.status === 'COMPLETED' && invitation.familyAnswers?.length > 0;

const JobInvitations = (props) => {
  const {invitations, invitationsError, jobBackend} = props;

  const [cancelTarget, setCancelTarget] = React.useState(null);
  const [expandedIds, setExpandedIds] = React.useState([]);

  const toggleExpanded = (invitationId) => {
    setExpandedIds((current) => (
      current.includes(invitationId) ?
        current.filter((id) => id !== invitationId) :
        [...current, invitationId]
    ));
  };

  return (
    <Stack spacing={2} pb={2}>
      <Divider />
      {
        invitationsError &&
        <Alert severity="error" sx={{mx: 2}}>
          <Translate expression="career.jobDetail.Invitations.Errors.Load" />
        </Alert>
      }
      {
        !invitationsError && invitations.length === 0 &&
        <Typography color="text.secondary" align="center">
          <Translate expression="career.jobDetail.Invitations.Empty" />
        </Typography>
      }
      {
        invitations.length > 0 &&
        <Box sx={{overflowX: 'auto', pb: 1}}>
          <Box sx={{minWidth: 1150}}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: COLUMNS,
                gap: 1.5,
                alignItems: 'center',
                backgroundColor: '#F9F8F6',
                borderTop: '1px solid',
                borderBottom: '1px solid',
                borderColor: 'divider',
                px: 2,
                py: 1,
              }}
            >
              {
                Object.keys(translate('career.jobDetail.Invitations.Columns')).map((column) => (
                  <Typography key={column} variant="caption" fontWeight={700}>
                    <Translate expression={`career.jobDetail.Invitations.Columns.${column}`} />
                  </Typography>
                ))
              }
              <Box />
            </Box>
            {
              invitations.map((invitation) => (
                <Box
                  key={invitation.id}
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: COLUMNS,
                      gap: 1.5,
                      alignItems: 'center',
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    <Typography fontWeight={600}>
                      {caregiverName(invitation)}
                    </Typography>
                    <Box>
                      <Chip
                        label={<Translate expression={`career.jobDetail.Invitations.Status.${invitation.status}`} />}
                        color={{WAITING: 'info', COMPLETED: 'success', DECLINED: 'warning'}[invitation.status] || 'default'}
                        size="small"
                      />
                    </Box>
                    <Typography variant="body2">
                      {
                        (invitation.workflow?.steadyState || invitation.status === 'WAITING') &&
                        <Translate expression={`career.jobDetail.Invitations.Progress.${invitation.workflow?.steadyState || 'STARTING'}`} />
                      }
                    </Typography>
                    <Typography variant="body2">
                      {moment(invitation.createdAt).format('MMM D, YYYY h:mm A')}
                    </Typography>
                    <Typography variant="body2">
                      {
                        invitation.closedAt &&
                        moment(invitation.closedAt).format('MMM D, YYYY h:mm A')
                      }
                    </Typography>
                    <Stack spacing={0.25}>
                      {
                        invitation.declineReason &&
                        <Typography variant="body2">
                          <Translate expression="career.jobDetail.Invitations.DeclineReason" substitute={{reason: invitation.declineReason}} />
                        </Typography>
                      }
                      {
                        invitation.requestedDoNotContact &&
                        <Typography variant="body2" color="error.main">
                          <Translate expression="career.jobDetail.Invitations.DoNotContact" />
                        </Typography>
                      }
                    </Stack>
                    <Box sx={{textAlign: 'right'}}>
                      {
                        invitation.status === 'WAITING' &&
                        <Button size="small" variant="outlined" color="error" onClick={() => setCancelTarget(invitation)}>
                          <Translate expression="career.jobDetail.Invitations.Cancel" />
                        </Button>
                      }
                      {
                        hasFamilyAnswers(invitation) &&
                        <IconButton
                          size="small"
                          aria-expanded={expandedIds.includes(invitation.id)}
                          aria-label={translate(
                              `career.jobDetail.Invitations.${expandedIds.includes(invitation.id) ? 'HideAnswers' : 'ShowAnswers'}`,
                              {name: caregiverName(invitation)},
                          )}
                          onClick={() => toggleExpanded(invitation.id)}
                        >
                          {expandedIds.includes(invitation.id) ? <CollapseIcon /> : <ExpandIcon />}
                        </IconButton>
                      }
                    </Box>
                  </Box>
                  {
                    hasFamilyAnswers(invitation) && expandedIds.includes(invitation.id) &&
                    <Box sx={{px: 2, pb: 2, maxWidth: '900px'}}>
                      <InvitationFamilyAnswers
                        invitation={invitation}
                        caregiverName={caregiverName(invitation)}
                        onShared={jobBackend.invitations.reload}
                      />
                    </Box>
                  }
                </Box>
              ))
            }
          </Box>
        </Box>
      }
      {
        cancelTarget &&
        <Dialog onClose={() => setCancelTarget(null)}>
          <DialogTitle onClose={() => setCancelTarget(null)}>
            <Translate expression="career.jobDetail.Invitations.CancelTitle" />
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" pt={1}>
              <Translate
                expression="career.jobDetail.Invitations.CancelConfirm"
                substitute={{
                  name: caregiverName(cancelTarget),
                }}
              />
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setCancelTarget(null)}>
              <Translate expression="career.jobDetail.Invitations.Keep" />
            </Button>
            <Button
              color="error"
              onClick={() => {
                setCancelTarget(null);
                jobBackend.invitations.cancel(cancelTarget);
              }}
            >
              <Translate expression="career.jobDetail.Invitations.Cancel" />
            </Button>
          </DialogActions>
        </Dialog>
      }
    </Stack>
  );
};

JobInvitations.propTypes = {
  invitations: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    declineReason: PropTypes.string,
    requestedDoNotContact: PropTypes.bool,
    closedAt: PropTypes.string,
    createdAt: PropTypes.string.isRequired,
    caregiver: PropTypes.shape({
      person: PropTypes.shape({
        firstName: PropTypes.string.isRequired,
        lastName: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
    workflow: PropTypes.shape({
      steadyState: PropTypes.string,
    }),
    familyAnswers: PropTypes.array,
    sharedWithFamilyAt: PropTypes.string,
  })).isRequired,
  invitationsError: PropTypes.object,
  jobBackend: PropTypes.shape({
    invitations: PropTypes.shape({
      cancel: PropTypes.func.isRequired,
      reload: PropTypes.func.isRequired,
    }).isRequired,
  }).isRequired,
};

export default React.memo(JobInvitations);
